/**
 * AdminPages.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import PagePreviewModal from './PagePreviewModal';
import HelpPopup from './HelpPopup';
import LoadingSpinner from './LoadingSpinner';
import { queryMatchesSearchText, tuneRowSearchHaystack } from '../utils/siteSearch';
import './AdminPages.css';

const SORT_COL = {
  PAGE_ID: 'page_id',
  TITLE: 'title',
  TYPE: 'page_type',
  VERSION: 'version',
  UPDATED: 'updated_at',
};

/** page_content + content_pages can overlap; prefer content_pages row (listed second in API). */
function dedupeAdminPages(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (row?.page_id) byId.set(row.page_id, row);
  });
  return Array.from(byId.values());
}

function compareAdminPages(a, b, sortColumn, dir) {
  let cmp = 0;
  switch (sortColumn) {
    case SORT_COL.PAGE_ID: {
      const va = String(a.page_id || '').toLowerCase();
      const vb = String(b.page_id || '').toLowerCase();
      cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      break;
    }
    case SORT_COL.TITLE: {
      const va = String(a.title || a.page_id || '').toLowerCase();
      const vb = String(b.title || b.page_id || '').toLowerCase();
      cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      break;
    }
    case SORT_COL.TYPE: {
      const va = String(a.page_type || '').toLowerCase();
      const vb = String(b.page_type || '').toLowerCase();
      cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      break;
    }
    case SORT_COL.VERSION: {
      const va = parseInt(a.version, 10);
      const vb = parseInt(b.version, 10);
      const na = Number.isFinite(va) ? va : 0;
      const nb = Number.isFinite(vb) ? vb : 0;
      cmp = na - nb;
      break;
    }
    case SORT_COL.UPDATED: {
      const ta = new Date(a.updated_at || 0).getTime();
      const tb = new Date(b.updated_at || 0).getTime();
      cmp = ta - tb;
      break;
    }
    default:
      cmp = 0;
  }
  if (cmp !== 0) return cmp * dir;
  return String(a.page_id || '').localeCompare(String(b.page_id || ''), undefined, {
    sensitivity: 'base',
  }) * dir;
}

function SortableColumnHeader({ label, column, sortColumn, sortDirection, onSort }) {
  const active = sortColumn === column;
  return (
    <th
      scope="col"
      className="admin-pages-sortable"
      aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`admin-pages-sort-btn${active ? ' admin-pages-sort-btn--active' : ''}`}
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        {active ? (
          <span className="admin-pages-sort-indicator" aria-hidden>
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        ) : null}
      </button>
    </th>
  );
}

const AdminPages = () => {
  const { showSuccess, showError } = useToast();
  const { logout } = useAuth();
  const [pages, setPages] = useState([]);
  const [filteredPages, setFilteredPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPageId, setPreviewPageId] = useState(null);
  const [showUrlConverter, setShowUrlConverter] = useState(false);
  const [urlToConvert, setUrlToConvert] = useState('');
  const [convertingUrl, setConvertingUrl] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedPages, setSelectedPages] = useState(new Set());
  /** null = keep API order after filter */
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [showImportZipModal, setShowImportZipModal] = useState(false);
  const importZipFileInputRef = React.useRef(null);
  const [newPage, setNewPage] = useState({
    pageId: '',
    title: '',
    description: '',
    type: 'general',
    html: ''
  });
  
  // Duplicate checking states
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicatePages, setDuplicatePages] = useState([]);
  const [pendingPageData, setPendingPageData] = useState(null);

  // Predefined page types with descriptions
  const predefinedPageTypes = [
    { value: 'general', label: 'General Page', description: 'Standard content page' },
    { value: 'product-detail', label: 'Product Detail', description: 'Detailed product information page' },
    { value: 'tune-notation', label: 'Tune Notation', description: 'Musical notation and sheet music' },
    { value: 'abc-notation', label: 'ABC Notation', description: 'ABC musical notation format' },
    { value: 'tutorial', label: 'Tutorial', description: 'Educational content and guides' },
    { value: 'news', label: 'News Article', description: 'News and announcements' },
    { value: 'article', label: 'Article', description: 'Historical articles and cultural content' },
    { value: 'biography', label: 'Biography', description: 'Artist or musician biographies' },
    { value: 'recording', label: 'Recording', description: 'Album or recording pages' },
    { value: 'review', label: 'Review', description: 'Product or recording reviews' },
    { value: 'about', label: 'About Page', description: 'About us or company information' }
  ];

  // Get all unique page types from existing pages
  const existingPageTypes = [...new Set(pages.map(p => p.page_type).filter(Boolean))];
  
  // Combine predefined and existing types
  const allPageTypes = [
    ...predefinedPageTypes,
    // Add any existing types that aren't in predefined list
    ...existingPageTypes
      .filter(type => !predefinedPageTypes.some(pt => pt.value === type))
      .map(type => ({ 
        value: type, 
        label: type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        description: 'Custom page type'
      }))
  ];

  useEffect(() => {
    loadPages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter pages based on search term and page type
  useEffect(() => {
    let filtered = pages;

    // Filter by search term (token rules aligned with site / page editor pickers)
    if (searchTerm.trim()) {
      filtered = filtered.filter(page =>
        queryMatchesSearchText(tuneRowSearchHaystack(page), searchTerm),
      );
    }

    // Filter by page type
    if (filterType !== 'all') {
      filtered = filtered.filter(page => page.page_type === filterType);
    }

    setFilteredPages(filtered);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [pages, searchTerm, filterType]);

  const handleSortClick = useCallback((column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        column,
        direction: column === SORT_COL.VERSION || column === SORT_COL.UPDATED ? 'desc' : 'asc',
      };
    });
    setCurrentPage(1);
  }, []);

  const sortedFilteredPages = useMemo(() => {
    const { column: sortColumn, direction: sortDirection } = sortConfig;
    if (!sortColumn) return filteredPages;
    const dir = sortDirection === 'asc' ? 1 : -1;
    const list = [...filteredPages];
    list.sort((a, b) => compareAdminPages(a, b, sortColumn, dir));
    return list;
  }, [filteredPages, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedFilteredPages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPages = sortedFilteredPages.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Check for duplicate pages
  const checkForDuplicates = (pageData) => {
    const duplicates = [];
    const pageIdLower = pageData.pageId.toLowerCase().trim();
    const titleLower = pageData.title.toLowerCase().trim();
    
    pages.forEach(page => {
      const reasons = [];
      
      // Check for exact page ID match
      if (page.page_id.toLowerCase().trim() === pageIdLower) {
        reasons.push('Same Page ID');
      }
      
      // Check for exact title match
      if (page.title && page.title.toLowerCase().trim() === titleLower) {
        reasons.push('Exact title match');
      }
      
      // Check for similar title (>85% similar)
      if (page.title) {
        const similarity = calculateSimilarity(page.title.toLowerCase(), titleLower);
        if (similarity > 0.85 && similarity < 1.0) {
          reasons.push(`Similar title (${Math.round(similarity * 100)}% match)`);
        }
      }
      
      // Check for similar page ID (>80% similar)
      const idSimilarity = calculateSimilarity(page.page_id.toLowerCase(), pageIdLower);
      if (idSimilarity > 0.8 && idSimilarity < 1.0) {
        reasons.push(`Similar Page ID (${Math.round(idSimilarity * 100)}% match)`);
      }
      
      if (reasons.length > 0) {
        duplicates.push({
          page,
          reasons
        });
      }
    });
    
    return duplicates;
  };
  
  // Simple string similarity calculation (Dice coefficient)
  const calculateSimilarity = (str1, str2) => {
    const bigrams1 = getBigrams(str1);
    const bigrams2 = getBigrams(str2);
    const intersection = bigrams1.filter(x => bigrams2.includes(x));
    return (2.0 * intersection.length) / (bigrams1.length + bigrams2.length);
  };
  
  const getBigrams = (str) => {
    const bigrams = [];
    for (let i = 0; i < str.length - 1; i++) {
      bigrams.push(str.substring(i, i + 2));
    }
    return bigrams;
  };

  // Handle proceeding despite duplicate warning
  const handleProceedWithCreate = () => {
    setShowDuplicateWarning(false);
    setDuplicatePages([]);
    if (pendingPageData) {
      proceedWithPageCreation(pendingPageData);
    }
    setPendingPageData(null);
  };

  const handleCancelDuplicateWarning = () => {
    setShowDuplicateWarning(false);
    setDuplicatePages([]);
    setPendingPageData(null);
  };

  const proceedWithPageCreation = (pageData) => {
    // Navigate to page editor
    const queryParams = new URLSearchParams({
      pageId: pageData.pageId,
      title: pageData.title,
      description: pageData.description,
      type: pageData.type || 'general'
    });
    
    window.location.href = `/admin/pages/edit?${queryParams.toString()}`;
  };

  const loadPages = async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      console.log('Using token:', token ? token.substring(0, 50) + '...' : 'No token found');
      if (!token) {
        showError('Authentication required. Please login first.');
        return;
      }
      
      // Add cache busting to prevent stale data after delete
      const cacheBuster = new Date().getTime();
      const response = await fetch(`/api/content/pages?_=${cacheBuster}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
          console.log('401 Unauthorized - token expired, logging out');
          logout();
          return; // Exit early, logout will redirect
        }
        
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error(`Failed to load pages: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 500)); // Log first 500 chars
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();
      setPages(dedupeAdminPages(data));
    } catch (error) {
      console.error('Error loading pages:', error);
      showError(`Failed to load pages: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = () => {
    if (!newPage.pageId.trim()) {
      showError('Page ID is required');
      return;
    }

    if (!newPage.title.trim()) {
      showError('Page title is required');
      return;
    }

    // Validate page ID format (alphanumeric with hyphens)
    const pageIdRegex = /^[a-z0-9-]+$/;
    if (!pageIdRegex.test(newPage.pageId)) {
      showError('Page ID must contain only lowercase letters, numbers, and hyphens');
      return;
    }

    // Validate page type format (if provided)
    if (newPage.type && newPage.type.trim()) {
      const pageTypeRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
      if (!pageTypeRegex.test(newPage.type.trim())) {
        showError('Page type must be lowercase letters, numbers, and hyphens only (e.g., "my-custom-type")');
        return;
      }
    }

    // Check for duplicates
    const duplicates = checkForDuplicates(newPage);
    
    if (duplicates.length > 0) {
      // Show duplicate warning modal
      setDuplicatePages(duplicates);
      setPendingPageData(newPage);
      setShowDuplicateWarning(true);
      return; // Stop creation until user confirms
    }

    // If no duplicates, proceed with creation
    proceedWithPageCreation(newPage);
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm(`Are you sure you want to delete the page "${pageId}"?`)) {
      return;
    }

    // Optimistic update: remove from UI immediately
    const previousPages = [...pages];
    setPages(pages.filter(page => page.page_id !== pageId));

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please login first.');
        // Restore previous state on error
        setPages(previousPages);
        return;
      }

      const response = await fetch(`/api/content/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
          console.log('401 Unauthorized - token expired, logging out');
          logout();
          return; // Exit early, logout will redirect
        }
        
        // Restore previous state on error
        setPages(previousPages);
        throw new Error('Failed to delete page');
      }

      showSuccess('Page deleted successfully');
      // Reload to ensure consistency with database
      loadPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      showError('Failed to delete page');
      // Previous state already restored in the error handling above
    }
  };

  const getPageTypeLabel = (type) => {
    const pageType = allPageTypes.find(pt => pt.value === type);
    if (pageType) {
      return pageType.label;
    }
    // Format unknown types nicely (e.g., "my-custom-type" -> "My Custom Type")
    return type ? type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'General Page';
  };

  const handleViewPage = (pageId) => {
    setPreviewPageId(pageId);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewPageId(null);
  };

  const handleUrlConversion = async () => {
    if (!urlToConvert.trim()) {
      showError('Please enter a URL to convert');
      return;
    }

    setConvertingUrl(true);
    try {
      const response = await fetch('/api/content/convert-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: urlToConvert })
      });

      if (!response.ok) {
        throw new Error('Failed to convert URL');
      }

      const result = await response.json();
      
      // Pre-fill the create form with the converted content
      setNewPage({
        pageId: result.pageId || '',
        title: result.title || '',
        description: result.description || '',
        type: result.type || 'article',
        html: result.html || ''
      });
      
      setShowUrlConverter(false);
      setShowCreateForm(true);
      setUrlToConvert('');
      showSuccess('URL converted successfully! Please review and save the page.');
      
    } catch (error) {
      console.error('Error converting URL:', error);
      showError('Failed to convert URL. Please check the URL and try again.');
    } finally {
      setConvertingUrl(false);
    }
  };

  const togglePageSelection = (pageId) => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageId)) {
      newSelection.delete(pageId);
    } else {
      newSelection.add(pageId);
    }
    setSelectedPages(newSelection);
  };

  const toggleSelectAllPages = () => {
    if (selectedPages.size === paginatedPages.length) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set(paginatedPages.map(p => p.page_id)));
    }
  };

  const handleExportSelected = async () => {
    if (selectedPages.size === 0) {
      showError('Please select at least one page to export');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/content/pages/export/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pageIds: Array.from(selectedPages) })
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'pages_export.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSuccess(`${selectedPages.size} page(s) exported successfully`);
    } catch (error) {
      console.error('Error exporting pages:', error);
      showError('Failed to export pages');
    }
  };

  const handleImportZipClick = () => {
    setShowImportZipModal(true);
  };

  const handleImportZipConfirm = () => {
    setShowImportZipModal(false);
    importZipFileInputRef.current?.click();
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('zipfile', file);
      
      const response = await fetch('/api/content/pages/import/bulk', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }
      
      const result = await response.json();
      
      const summary = [];
      if (result.pages_imported > 0) summary.push(`${result.pages_imported} pages created`);
      if (result.pages_updated > 0) summary.push(`${result.pages_updated} pages updated`);
      if (result.assets_created > 0) summary.push(`${result.assets_created} assets created`);
      if (result.assets_updated > 0) summary.push(`${result.assets_updated} assets updated`);
      
      showSuccess(`Import completed: ${summary.join(', ')}`);
      
      if (result.errors && result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
        showError(`Some items had errors. Check console for details.`);
      }
      
      loadPages();
    } catch (error) {
      console.error('Error importing ZIP:', error);
      showError('Failed to import pages: ' + error.message);
    }
    
    // Reset file input
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="admin-pages-container container">
        <div className="admin-pages-content content-card">
          <LoadingSpinner size="medium" message="Loading pages..." />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-pages-container container">
      <div className="admin-pages-content content-card">
        <div className="admin-pages-header">
          <h2>Page Management</h2>
          <div className="admin-pages-actions">
            {selectedPages.size > 0 && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button 
                  className="btn btn-success"
                  onClick={handleExportSelected}
                >
                  📦 Export Selected ({selectedPages.size})
                </button>
                <HelpPopup title="Export Pages as ZIP" position="bottom">
                  <p><strong>Export selected pages</strong> with all content and associated assets.</p>
                  
                  <p><strong>What's included in the ZIP:</strong></p>
                  <ul>
                    <li><code>manifest.json</code> - Page metadata, HTML content, and asset references</li>
                    <li><code>assets/</code> - All images, audio files, etc. used in the pages</li>
                    <li><code>README.txt</code> - Import instructions</li>
                  </ul>
                  
                  <div className="tip">
                    💡 <strong>Use Case:</strong> Perfect for migrating content between environments or creating backups of important pages.
                  </div>
                  
                  <p><strong>To import later:</strong> Use the "📦 Import ZIP" button with the exported file.</p>
                </HelpPopup>
              </div>
            )}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button 
                className="btn btn-sm btn-info"
                onClick={handleImportZipClick}
                title="Import pages from ZIP"
              >
                📦 Import ZIP
              </button>
              <HelpPopup title="Import Pages from ZIP" position="bottom">
                <p><strong>Import pages from a ZIP file</strong> previously exported from this system.</p>
                
                <p><strong>What happens during import:</strong></p>
                <ul>
                  <li>Reads <code>manifest.json</code> for page metadata and content</li>
                  <li>Creates or updates pages in the database</li>
                  <li>Automatically imports all associated assets (images, audio, etc.)</li>
                  <li>Preserves page IDs, content versions, and metadata</li>
                </ul>
                
                <div className="tip">
                  💡 <strong>Smart Handling:</strong> Assets are created if they don't exist, or reused if they're already in the system.
                </div>
                
                <p><strong>After import:</strong> You'll see a summary showing pages imported/updated and assets created/reused.</p>
              </HelpPopup>
            </div>
            <button 
              className="btn btn-outline"
              onClick={() => setShowUrlConverter(true)}
            >
              {getIcon('link')} Convert URL
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {getIcon('plus')} Create New Page
            </button>
          </div>
        </div>

        {/* Hidden file input for ZIP import */}
        <input
          type="file"
          ref={importZipFileInputRef}
          style={{ display: 'none' }}
          onChange={handleZipUpload}
          accept=".zip"
        />

        {showCreateForm && (
          <div className="create-page-form">
            <h3>
              Create New Page
              <HelpPopup title="Page Creation Guide" position="bottom">
                <p><strong>Pages</strong> are the building blocks of your website content. Each page has a unique ID and can contain rich HTML content.</p>
                
                <div className="tip">
                  💡 <strong>Workflow:</strong> Create the page shell here, then use the Page Editor to add content and metadata.
                </div>
                
                <p><strong>Page Types Available:</strong></p>
                <ul>
                  <li><strong>General:</strong> Standard content pages</li>
                  <li><strong>Tune Notation:</strong> Music notation with audio</li>
                  <li><strong>Article:</strong> Blog posts or articles</li>
                  <li><strong>Product:</strong> Product detail pages</li>
                  <li><strong>Biography:</strong> Artist/composer profiles</li>
                </ul>
                
                <div className="warning">
                  ⚠️ <strong>Page ID cannot be changed</strong> after creation. Choose carefully!
                </div>
              </HelpPopup>
            </h3>
            <div className="form-group">
              <label htmlFor="pageId">
                Page ID *
                <HelpPopup title="Understanding Page IDs" position="right">
                  <p><strong>Page ID</strong> is the unique identifier used in the URL.</p>
                  
                  <div className="example-box">
                    <strong>Example:</strong><br/>
                    Page ID: <code>silver-apple-jig</code><br/>
                    URL: <code>yoursite.com/tunes/silver-apple-jig</code>
                  </div>
                  
                  <p><strong>Rules:</strong></p>
                  <ul>
                    <li>Use lowercase letters only</li>
                    <li>Replace spaces with hyphens (-)</li>
                    <li>No special characters</li>
                    <li>Keep it descriptive and SEO-friendly</li>
                  </ul>
                  
                  <div className="warning">
                    ⚠️ Cannot be changed after creation!
                  </div>
                </HelpPopup>
              </label>
              <input
                type="text"
                id="pageId"
                value={newPage.pageId}
                onChange={(e) => setNewPage({...newPage, pageId: e.target.value})}
                placeholder="e.g., product-detail-overview"
                className="form-control"
              />
              <small>Use lowercase letters, numbers, and hyphens only</small>
            </div>

            <div className="form-group">
              <label htmlFor="title">Page Title *</label>
              <input
                type="text"
                id="title"
                value={newPage.title}
                onChange={(e) => setNewPage({...newPage, title: e.target.value})}
                placeholder="e.g., Product Detail Overview"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={newPage.description}
                onChange={(e) => setNewPage({...newPage, description: e.target.value})}
                placeholder="Brief description of the page content"
                className="form-control"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="type">
                Page Type
                <HelpPopup title="Page Types Explained" position="right">
                  <p><strong>Page types</strong> determine metadata fields and how the page is rendered.</p>
                  
                  <p><strong>Common Types:</strong></p>
                  <ul>
                    <li><strong>tune-notation:</strong> For tunes with music notation, audio, and metadata (artist, tune type, key)</li>
                    <li><strong>article:</strong> For blog posts and articles with author and publication date</li>
                    <li><strong>product:</strong> For product detail pages with SKU and pricing</li>
                    <li><strong>general:</strong> For standard content without special metadata</li>
                  </ul>
                  
                  <div className="tip">
                    💡 The Page Editor will show type-specific metadata fields based on your selection.
                  </div>
                  
                  <p>You can also create custom types for your specific needs!</p>
                </HelpPopup>
              </label>
              <input
                type="text"
                id="type"
                list="pageTypesList"
                value={newPage.type}
                onChange={(e) => setNewPage({...newPage, type: e.target.value})}
                placeholder="Select or type a custom page type"
                className="form-control"
              />
              <datalist id="pageTypesList">
                {allPageTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </datalist>
              <small>
                Select from suggestions or type a custom page type (e.g., "biography", "recording", "review").
                Use lowercase with hyphens (e.g., "my-custom-type").
              </small>
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-primary"
                onClick={handleCreatePage}
              >
                Create Page
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showUrlConverter && (
          <div className="url-converter-modal">
            <div className="url-converter-content">
              <div className="url-converter-header">
                <h3>Convert URL to Article</h3>
                <button 
                  className="btn-close"
                  onClick={() => setShowUrlConverter(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="url-converter-body">
                <p>Enter a URL to convert it into an article. This tool will extract content from the URL and create a new article page.</p>
                
                <div className="form-group">
                  <label htmlFor="urlInput">URL to Convert *</label>
                  <input
                    type="url"
                    id="urlInput"
                    value={urlToConvert}
                    onChange={(e) => setUrlToConvert(e.target.value)}
                    placeholder="https://www.cranfordpub.com/articles/copyrights.htm"
                    className="form-control"
                  />
                </div>
                
                <div className="url-converter-actions">
                  <button 
                    className="btn btn-outline"
                    onClick={() => setShowUrlConverter(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleUrlConversion}
                    disabled={convertingUrl || !urlToConvert.trim()}
                  >
                    {convertingUrl ? (
                      <>
                        <LoadingSpinner size="small" /> Converting...
                      </>
                    ) : (
                      <>
                        {getIcon('link')} Convert URL
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Warning Modal */}
        {showDuplicateWarning && duplicatePages.length > 0 && (
          <div className="url-converter-modal">
            <div className="url-converter-content" style={{ maxWidth: '700px' }}>
              <div className="url-converter-header">
                <h3>⚠️ Possible Duplicate Pages Detected</h3>
                <button 
                  className="btn-close"
                  onClick={handleCancelDuplicateWarning}
                >
                  ×
                </button>
              </div>
              
              <div className="url-converter-body">
                <div style={{ padding: '20px' }}>
                  <p style={{ 
                    fontSize: '14px',
                    color: '#555',
                    marginBottom: '25px',
                    lineHeight: '1.6'
                  }}>
                    We found <strong style={{ color: '#ff9800' }}>{duplicatePages.length}</strong> page(s) that may be duplicates of the one you're trying to create. 
                    Please review and confirm if you want to proceed.
                  </p>
                  
                  {/* Page being created */}
                  <div style={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '2px solid #ff9800',
                    borderRadius: '8px',
                    padding: '18px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px'
                    }}>
                      <span style={{ fontSize: '18px' }}>📄</span>
                      <strong style={{ fontSize: '14px' }}>Page You're Creating</strong>
                    </div>
                    <div style={{ paddingLeft: '28px', fontSize: '13px', color: '#555' }}>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>Page ID:</strong> {pendingPageData?.pageId}
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>Title:</strong> {pendingPageData?.title}
                      </div>
                      <div>
                        <strong>Type:</strong> {getPageTypeLabel(pendingPageData?.type)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Possible duplicates */}
                  <div style={{ marginBottom: '25px' }}>
                    <strong style={{
                      fontSize: '14px',
                      display: 'block',
                      marginBottom: '12px'
                    }}>
                      Possible Duplicates Found:
                    </strong>
                    
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {duplicatePages.map((dup, index) => (
                        <div key={dup.page.page_id} style={{
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '12px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px'
                              }}>
                                {dup.page.title || dup.page.page_id}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                <strong>Page ID:</strong> {dup.page.page_id} | <strong>Type:</strong> {getPageTypeLabel(dup.page.page_type)}
                              </div>
                              <div style={{ 
                                fontSize: '12px',
                                color: '#ff9800',
                                marginTop: '8px'
                              }}>
                                <strong>Match reasons:</strong> {dup.reasons.join(', ')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '25px'
                  }}>
                    <div style={{ fontSize: '13px', color: '#1976d2', lineHeight: '1.6' }}>
                      💡 <strong>Tip:</strong> Review the pages above carefully. If this is truly a new page, click "Create Anyway". Otherwise, cancel and edit the existing page instead.
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="url-converter-actions">
                  <button 
                    className="btn btn-outline"
                    onClick={handleCancelDuplicateWarning}
                  >
                    ✕ Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleProceedWithCreate}
                    style={{ backgroundColor: '#ff9800', borderColor: '#ff9800' }}
                  >
                    ✓ Create Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showImportZipModal && (
          <div className="url-converter-modal">
            <div className="url-converter-content">
              <div className="url-converter-header">
                <h3>Import Pages from ZIP</h3>
                <button 
                  className="btn-close"
                  onClick={() => setShowImportZipModal(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="url-converter-body">
                <div className="zip-import-wrapper">
                  <div className="zip-import-info-box">
                    <div className="zip-import-title">📦 ZIP Import</div>
                    <div className="zip-import-content">
                      Import pages with their HTML content, metadata, and referenced assets from a ZIP archive previously exported from this system.
                      <br /><br />
                      The ZIP should contain:
                      <ul>
                        <li><code>manifest.json</code> - Page metadata and HTML content</li>
                        <li><code>assets/</code> folder - Referenced images and files</li>
                      </ul>
                      <br />
                      <strong>Import behavior:</strong>
                      <ul>
                        <li>Pages with matching page_id will be <strong>updated</strong> (version incremented)</li>
                        <li>New pages will be <strong>created</strong></li>
                        <li>Assets are imported and HTML references are updated automatically</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="url-converter-actions">
                  <button 
                    className="btn btn-outline"
                    onClick={() => setShowImportZipModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleImportZipConfirm}
                  >
                    📦 Choose ZIP File
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pages-list">
          <div className="pages-list-header-compact">
            <h3>
              📄 Pages
              <span className="header-stats">
                <span>Total: {pages.length}</span>
                {filteredPages.length !== pages.length && <span>Filtered: {filteredPages.length}</span>}
                <span>Page: {startIndex + 1}-{Math.min(endIndex, sortedFilteredPages.length)}</span>
              </span>
            </h3>
            
            {/* Filters Bar */}
            <div className="pages-filters-compact">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="🔍 Search pages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control-sm"
                />
              </div>
              
              <div className="filter-box">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="form-control-sm"
                >
                  <option value="all">📋 All Types</option>
                  {allPageTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {(searchTerm || filterType !== 'all') && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                    setSortConfig({ column: null, direction: 'asc' });
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>
          
          {sortedFilteredPages.length === 0 ? (
            <div className="no-pages">
              <p>
                {pages.length === 0 
                  ? "No pages found. Create your first page to get started."
                  : "No pages match your search criteria. Try adjusting your filters."
                }
              </p>
            </div>
          ) : (
            <>
              <div className="pages-table-container">
                <table className="pages-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedPages.size === paginatedPages.length && paginatedPages.length > 0}
                          onChange={toggleSelectAllPages}
                          title="Select all pages on this page"
                        />
                      </th>
                      <SortableColumnHeader
                        label="Page ID"
                        column={SORT_COL.PAGE_ID}
                        sortColumn={sortConfig.column}
                        sortDirection={sortConfig.direction}
                        onSort={handleSortClick}
                      />
                      <SortableColumnHeader
                        label="Title"
                        column={SORT_COL.TITLE}
                        sortColumn={sortConfig.column}
                        sortDirection={sortConfig.direction}
                        onSort={handleSortClick}
                      />
                      <SortableColumnHeader
                        label="Type"
                        column={SORT_COL.TYPE}
                        sortColumn={sortConfig.column}
                        sortDirection={sortConfig.direction}
                        onSort={handleSortClick}
                      />
                      <SortableColumnHeader
                        label="Version"
                        column={SORT_COL.VERSION}
                        sortColumn={sortConfig.column}
                        sortDirection={sortConfig.direction}
                        onSort={handleSortClick}
                      />
                      <SortableColumnHeader
                        label="Updated"
                        column={SORT_COL.UPDATED}
                        sortColumn={sortConfig.column}
                        sortDirection={sortConfig.direction}
                        onSort={handleSortClick}
                      />
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPages.map((page) => (
                      <tr key={page.page_id}>
                        <td className="page-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedPages.has(page.page_id)}
                            onChange={() => togglePageSelection(page.page_id)}
                            title="Select page for export"
                          />
                        </td>
                        <td className="page-id-cell">
                          <code>{page.page_id}</code>
                        </td>
                        <td className="page-title-cell">
                          <div className="page-title-content">
                            <strong>{page.title || page.page_id}</strong>
                            {(page.is_hidden === 1 || page.is_hidden === true) && (
                              <span className="page-hidden-badge" title="Hidden from public site">Hidden</span>
                            )}
                            {page.description && (
                              <small className="page-description-preview">{page.description}</small>
                            )}
                          </div>
                        </td>
                        <td className="page-type-cell">
                          <span className="page-type-badge">{getPageTypeLabel(page.page_type)}</span>
                        </td>
                        <td className="page-version-cell">v{page.version}</td>
                        <td className="page-date-cell">
                          {new Date(page.updated_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="page-actions-cell">
                          <div className="page-actions-compact">
                            <button
                              className="btn-icon"
                              onClick={() => handleViewPage(page.page_id)}
                              title="View Page"
                            >
                              👁️
                            </button>
                            <Link 
                              to={`/admin/pages/edit?pageId=${page.page_id}`}
                              className="btn-icon"
                              title="Edit Page"
                            >
                              ✏️
                            </Link>
                            <button
                              className="btn-icon btn-danger"
                              onClick={() => handleDeletePage(page.page_id)}
                              title="Delete Page"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {startIndex + 1}-{Math.min(endIndex, sortedFilteredPages.length)} of {sortedFilteredPages.length} pages
                  </div>
                  
                  <div className="pagination-controls">
                    <button
                      className="btn btn-sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      ««
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹
                    </button>
                    
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            className={`btn btn-sm ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return <span key={pageNum} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                    
                    <button
                      className="btn btn-sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      »»
                    </button>
                  </div>
                  
                  <div className="pagination-per-page">
                    <label>
                      Per page:
                      <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="form-control-sm">
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <PagePreviewModal
        isOpen={showPreviewModal}
        onClose={handleClosePreview}
        pageId={previewPageId}
      />
    </div>
  );
};

export default AdminPages;

